import React, { useState } from "react";
import "./Projects.css";

const PROJECTS = [
  {
    id: "gitt",
    slug: "gitt",
    title: "GITT",
    subtitle: "Technology Workshop Inventory Management",
    role: "CI/CD & Full-Stack Developer",
    description:
      "Platform developed to centralize and optimize equipment management in technology workshops. GITT's core is a robust inventory manager that tracks the location, status, and assignment of every physical asset. The system automates stock control and evaluates equipment viability and depreciation through real-time financial calculations.\n\nAs part of the CI/CD pipeline, I architected a GitHub Actions workflow that automated the build process and pushed Docker images of the application to Docker Hub on every merge to main — ensuring continuous delivery with zero manual intervention.",
    tech: ["Next.js", "NestJS", "TypeScript", "React", "PostgreSQL", "Docker", "GitHub Actions"],
    images: [
      "/assets/projects/gitt_1.jpg",
      "/assets/projects/gitt_2.jpg",
      "/assets/projects/gitt_3.jpg",
      "/assets/projects/gitt_4.jpg",
    ],
    architecture: [
      {
        label: "Frontend (Next.js)",
        desc: "Interactive, highly responsive UI to facilitate rapid physical asset auditing and status report generation.",
      },
      {
        label: "Backend API (NestJS)",
        desc: "Modular architecture built in TypeScript to process strict business rules for loan control, returns, and user authorizations.",
      },
      {
        label: "CI/CD Pipeline",
        desc: "GitHub Actions workflow that automates the build and pushes Docker images to Docker Hub on every deployment.",
      },
      {
        label: "Infrastructure",
        desc: "Docker-ready deployment environment to ensure consistency and high availability on university servers.",
      },
    ],
    metrics: [
      "Strict Physical Inventory Management",
      "End-to-End Asset Traceability",
      "Access & Role Control",
      "Automated CI/CD Pipeline",
    ],
    links: {
      live: null,
      repo: null,
    },
    color: "--accent-cyan",
    icon: "⚙",
  },
  {
    id: "car",
    slug: "rent-a-car",
    title: "Rent-A-Car",
    subtitle: "ERP & Booking Engine",
    role: "Full-Stack Developer — Reservations Module",
    description:
      "Comprehensive system that digitizes the complete operation of a vehicle rental agency. Combines a customer portal for online reservations with a robust back-office for employees and administrators.\n\nI participated in both the frontend and backend of the reservations module. The system automates the entire rental cycle through a strict state machine: from booking and delivery (fuel/mileage logging), to return auditing, vehicle maintenance, and dynamic surcharge calculation for delays or damages.",
    tech: ["React", "NestJS", "TypeScript", "PostgreSQL", "Tailwind CSS", "Supabase"],
    images: [
      "/assets/projects/car_1.jpg",
      "/assets/projects/car_2.jpg",
      "/assets/projects/car_3.jpg",
      "/assets/projects/car_4.jpg",
    ],
    architecture: [
      {
        label: "State Machine & Business Logic",
        desc: "Rental lifecycle: Booking → In Progress → Pending → Paid/Cancelled with automatic final cost calculations.",
      },
      {
        label: "Multi-Role Frontend (React 18 + Vite)",
        desc: "SPA protected by a 4-level RBAC system (Admin, Employee, Customer, Guest) dynamically mutating UI permissions.",
      },
      {
        label: "Relational Backend (NestJS + PostgreSQL)",
        desc: "Normalized database maintaining referential integrity across catalog hierarchies, booking history, and maintenance.",
      },
      {
        label: "Serverless Media Management",
        desc: "Supabase Storage integration for optimized storage and distribution of the vehicle fleet image catalog.",
      },
    ],
    metrics: [
      "State Machine Lifecycle",
      "4-Level RBAC Architecture",
      "Dynamic Cost Calculation",
      "Normalized Database",
    ],
    links: {
      live: null,
      repo: null,
    },
    color: "--accent-yellow",
    icon: "🚗",
  },
  {
    id: "bus",
    slug: "chasquibus",
    title: "ChasquiBus",
    subtitle: "Multiplatform Bus Booking App",
    role: "Backend Developer — NestJS & PayPal Webhooks",
    description:
      "Multiplatform (web + mobile), multi-user application for bus ticket booking. The system supports three user roles: Driver, Assistant, and Passenger.\n\nI worked exclusively on the backend development using NestJS, implementing PayPal webhooks for real-time payment processing. The PostgreSQL database was deployed on Supabase, which also hosted the other services. The web frontend was built in React and the mobile app in React Native.",
    tech: ["NestJS", "TypeScript", "PostgreSQL", "Supabase", "React", "React Native", "PayPal Webhooks"],
    images: ["/assets/projects/bus_1.png"],
    architecture: [
      {
        label: "Backend (NestJS)",
        desc: "RESTful API with modular architecture, handling business logic for bookings, routes, and seat management.",
      },
      {
        label: "Payment Integration",
        desc: "PayPal Webhooks for real-time payment event processing — confirmations, refunds, and transaction status updates.",
      },
      {
        label: "Database (PostgreSQL on Supabase)",
        desc: "Cloud-hosted PostgreSQL database on Supabase for instant scalability and real-time capabilities.",
      },
      {
        label: "Frontend",
        desc: "Web interface built in React, mobile app in React Native — both consuming the same NestJS API.",
      },
    ],
    metrics: [
      "3-Role Multi-User System",
      "PayPal Webhook Integration",
      "Cross-Platform (Web + Mobile)",
      "Cloud DB on Supabase",
    ],
    links: {
      live: null,
      repo: null,
    },
    color: "--accent-green",
    icon: "🚌",
  },
  {
    id: "bio",
    slug: "biometric-auth",
    title: "Facial + Voice Auth",
    subtitle: "Multimodal Biometric Authentication",
    role: "Full-Stack Developer — ML Pipeline & Frontend",
    description:
      "Authentication platform that eliminates passwords through 1:N identification. The system captures real-time video from the browser, processes biometrics through deep neural networks on the backend, and compares generated embeddings against a high-speed vector database to authorize access in milliseconds.\n\nI built everything from the native capture interface to the model inference pipeline and database architecture.",
    tech: ["React", "TypeScript", "Python", "Django", "PostgreSQL", "pgvector", "Docker", "WebRTC"],
    images: [
      "/assets/projects/bio_1.jpg",
      "/assets/projects/bio_2.jpg",
      "/assets/projects/bio_3.jpg",
      "/assets/projects/bio_4.jpg",
    ],
    architecture: [
      {
        label: "Frontend (React + TypeScript)",
        desc: "Async media capture via WebRTC API and secure session handling with Axios Interceptors for JWT rotation.",
      },
      {
        label: "Backend (Python + Django)",
        desc: "Containerized RESTful API optimized to process video streams and run ML model inferences efficiently.",
      },
      {
        label: "Database (PostgreSQL + pgvector)",
        desc: "HNSW index implementation for ultra-fast spatial vector similarity searches.",
      },
      {
        label: "DL Models",
        desc: "Siamese Network (ResNet-50) for 128-dim facial embeddings and CNN-LSTM with attention for 256-dim voice embeddings.",
      },
    ],
    metrics: [
      "1:N Identification (No user input)",
      "HNSW Vector Search",
      "Multimodal Face + Voice Fusion",
      "100% Dockerized Topology",
    ],
    links: {
      live: null,
      repo: null,
    },
    color: "--accent-magenta",
    icon: "👁",
  },
];

const TECH_COLORS = {
  "Next.js": "#fff",
  "NestJS": "#e0234e",
  "TypeScript": "#3178c6",
  "React": "#61dafb",
  "React Native": "#61dafb",
  "PostgreSQL": "#336791",
  "Docker": "#2496ed",
  "GitHub Actions": "#2088ff",
  "Tailwind CSS": "#38bdf8",
  "Supabase": "#3ecf8e",
  "Python": "#ffd43b",
  "Django": "#092e20",
  "pgvector": "#336791",
  "WebRTC": "#ff6b35",
  "PayPal Webhooks": "#003087",
};

function TechBadge({ tech }) {
  const color = TECH_COLORS[tech] || "#00ffff";
  return (
    <span className="tech-badge" style={{ "--badge-color": color }}>
      {tech}
    </span>
  );
}

function MetricTag({ label }) {
  return <span className="metric-tag">{label}</span>;
}

function ImageGallery({ images, title }) {
  const [current, setCurrent] = useState(0);
  if (!images || images.length === 0) return null;
  return (
    <div className="proj-gallery">
      <div className="gallery-main">
        <img
          src={images[current]}
          alt={`${title} screenshot ${current + 1}`}
          className="gallery-main-img"
        />
        {images.length > 1 && (
          <>
            <button
              className="gallery-arrow gallery-arrow-left"
              onClick={() => setCurrent((c) => (c - 1 + images.length) % images.length)}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              className="gallery-arrow gallery-arrow-right"
              onClick={() => setCurrent((c) => (c + 1) % images.length)}
              aria-label="Next image"
            >
              ›
            </button>
          </>
        )}
        <div className="gallery-counter">
          {current + 1} / {images.length}
        </div>
      </div>
      {images.length > 1 && (
        <div className="gallery-thumbs">
          {images.map((img, i) => (
            <button
              key={i}
              className={`gallery-thumb${i === current ? " active" : ""}`}
              onClick={() => setCurrent(i)}
            >
              <img src={img} alt={`thumb ${i + 1}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectModal({ project, onClose }) {
  if (!project) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel"
        onClick={(e) => e.stopPropagation()}
        style={{ "--proj-color": `var(${project.color})` }}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-left">
            <div className="modal-bar">
              <span className="terminal-dot dot-red" />
              <span className="terminal-dot dot-yellow" />
              <span className="terminal-dot dot-green" />
              <span className="modal-bar-title">
                ~/projects/{project.slug} — detail
              </span>
            </div>
            <div className="modal-title-row">
              <span className="modal-icon">{project.icon}</span>
              <div>
                <h2 className="modal-title">{project.title}</h2>
                <p className="modal-subtitle">{project.subtitle}</p>
              </div>
            </div>
            <p className="modal-role">
              <span className="role-label">$ role </span>
              {project.role}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Description */}
          <div className="modal-section">
            <h3 className="modal-section-title">
              <span className="section-hash">#</span> description
            </h3>
            {project.description.split("\n\n").map((p, i) => (
              <p key={i} className="modal-desc-text">{p}</p>
            ))}
          </div>

          {/* Tech Stack */}
          <div className="modal-section">
            <h3 className="modal-section-title">
              <span className="section-hash">#</span> tech_stack
            </h3>
            <div className="tech-badges-row">
              {project.tech.map((t) => (
                <TechBadge key={t} tech={t} />
              ))}
            </div>
          </div>

          {/* Gallery */}
          <div className="modal-section">
            <h3 className="modal-section-title">
              <span className="section-hash">#</span> gallery
            </h3>
            <ImageGallery images={project.images} title={project.title} />
          </div>

          {/* Architecture */}
          <div className="modal-section">
            <h3 className="modal-section-title">
              <span className="section-hash">#</span> architecture_&amp;_design
            </h3>
            <div className="arch-grid">
              {project.architecture.map((a, i) => (
                <div key={i} className="arch-card">
                  <p className="arch-label">{a.label}</p>
                  <p className="arch-desc">{a.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Key Metrics */}
          <div className="modal-section">
            <h3 className="modal-section-title">
              <span className="section-hash">#</span> key_metrics
            </h3>
            <div className="metrics-row">
              {project.metrics.map((m, i) => (
                <MetricTag key={i} label={m} />
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="modal-section modal-links-section">
            <h3 className="modal-section-title">
              <span className="section-hash">#</span> links
            </h3>
            <div className="modal-links-row">
              {project.links.live ? (
                <a
                  href={project.links.live}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="proj-link-btn proj-link-live"
                >
                  <span>↗</span> Live Demo
                </a>
              ) : (
                <span className="proj-link-btn proj-link-disabled">
                  <span>↗</span> Live Demo — N/A
                </span>
              )}
              {project.links.repo ? (
                <a
                  href={project.links.repo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="proj-link-btn proj-link-repo"
                >
                  <span>⌥</span> Repository
                </a>
              ) : (
                <span className="proj-link-btn proj-link-disabled">
                  <span>⌥</span> Repository — Private
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ project, onOpen }) {
  return (
    <div
      className="proj-card"
      style={{ "--proj-color": `var(${project.color})` }}
      onClick={() => onOpen(project)}
      tabIndex={0}
      role="button"
      aria-label={`Open ${project.title} project`}
      onKeyDown={(e) => e.key === "Enter" && onOpen(project)}
    >
      <div className="proj-card-top">
        <div className="proj-card-header">
          <span className="proj-card-icon">{project.icon}</span>
          <div className="proj-card-titles">
            <h3 className="proj-card-name">{project.title}</h3>
            <p className="proj-card-subtitle">{project.subtitle}</p>
          </div>
        </div>
        <div className="proj-card-preview">
          <img
            src={project.images[0]}
            alt={`${project.title} preview`}
            className="proj-card-img"
          />
          <div className="proj-card-img-overlay" />
        </div>
        <p className="proj-card-role">
          <span className="role-sym">$</span> {project.role}
        </p>
        <div className="proj-card-tech">
          {project.tech.slice(0, 4).map((t) => (
            <TechBadge key={t} tech={t} />
          ))}
          {project.tech.length > 4 && (
            <span className="tech-more">+{project.tech.length - 4}</span>
          )}
        </div>
      </div>
      <div className="proj-card-footer">
        <div className="proj-card-metrics">
          {project.metrics.slice(0, 2).map((m, i) => (
            <span key={i} className="mini-metric">{m}</span>
          ))}
        </div>
        <button className="proj-card-cta">
          cat README.md <span className="cta-arrow">→</span>
        </button>
      </div>
    </div>
  );
}

const Projects = () => {
  const [activeProject, setActiveProject] = useState(null);

  return (
    <section className="projects-container" id="projects">
      <div className="section-cmd-header-p">
        <span className="cmd-prompt-p">❯</span>
        <span className="cmd-text-p">
          ls <span className="cmd-flag-p">./projects/</span>
        </span>
      </div>
      <h2 className="section-label-p">{'// Proyectos'}</h2>

      <div className="projects-terminal">
        <div className="projects-bar">
          <span className="terminal-dot dot-red" />
          <span className="terminal-dot dot-yellow" />
          <span className="terminal-dot dot-green" />
          <span className="projects-bar-title">projects — bash</span>
        </div>

        <div className="projects-body">
          <div className="proj-cmd-line">
            <span className="proj-prompt-sym">❯</span>
            <span className="proj-cmd">ls -la ./projects/ | grep drwx</span>
          </div>

          <div className="proj-output-line">
            {PROJECTS.map((p) => (
              <span key={p.id} className="proj-ls-entry">
                <span className="proj-flag">drwxr-xr-x</span>
                &nbsp;&nbsp;
                <span className="proj-size">4096</span>
                &nbsp;&nbsp;
                <span className="proj-name">{p.slug}/</span>
              </span>
            ))}
          </div>

          <div className="proj-cmd-line" style={{ marginTop: "1.2rem" }}>
            <span className="proj-prompt-sym">❯</span>
            <span className="proj-cmd">cat projects/*/README.md</span>
          </div>

          <div className="projects-grid">
            {PROJECTS.map((p) => (
              <ProjectCard key={p.id} project={p} onOpen={setActiveProject} />
            ))}
          </div>

          <div className="proj-cmd-line mt-proj">
            <span className="proj-prompt-sym">❯</span>
            <span className="cursor-blink">█</span>
          </div>
        </div>
      </div>

      <ProjectModal project={activeProject} onClose={() => setActiveProject(null)} />
    </section>
  );
};

export default Projects;
